import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import ProgrammesPicker from './programmes_picker';
import ProgrammesActionsPopupMenu from './programmes_actions_popup_menu';
import PlanNavigator from './plan_navigator';
import RecommenderWizardContainer from '../common/recommender_wizard_container';
import {
  PROGRAMME_PLANS_TITLE_PREFIX,
  emptyProgrammePlan,
  programmePlanPaginationIDs,
  programmeWithPlanAdded,
  programmesComparer,
} from '../../services/programmes';
import { userLocale } from '../../services/users';
import {
  useCreateContentEntryMutation,
  useCreateProgrammeMutation,
  useCreateSharedContentEntryMutation,
  useCreateSharedProgrammeMutation,
  useDeleteContentEntryMutation,
  useDeleteNoteMutation,
  useDeleteProgrammeMutation,
  useDeleteSharedProgrammeMutation,
  useGetContentEntriesByParentQuery,
  useGetProgrammesQuery,
  useGetSharedProgrammesQuery,
  useGetSpaceQuery,
  useLazyGetFullMealsByParentsQuery,
  useUpdateMealMutation,
  useUpdateProgrammeMutation,
  useUpdateSharedProgrammeMutation,
  useUpdateSpaceMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import { showGlobalSnackbar } from '../../features/global_snackbar/global_snackbar_slice';
import { ActivityObjectType } from '../../API';
import { useGetUserProfileQuery } from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import {
  duplicateProgramme,
  programmePlanRecommendMeals,
  publishLatestVersionOfProgramme,
  removeMealsAndGRCRecipesFromProgrammePlan,
  removePlan,
  removeProgramme,
  unpublishProgramme,
} from '../../operations/programmes_operations';
import { reportAccountActivitySignalOperation } from '../../operations/user_profile_operations';
import { userFriendlyNameForActivitySignalSelector } from '../../features/user_id/user_id_slice';
import { sendNotificationToAllDevicesOperation } from '../../operations/push_notification_operations';
import PublishConfirmationNotificationModal from '../creator_studio/publish_confirmation_notification_modal';

const TopNavBarMobileProgrammes = ({ selectedProgrammeId, selectedPlanId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data: userProfile } = useGetUserProfileQuery();

  const locale = userLocale(userProfile);

  const { data: healthProSpace } = useGetSpaceQuery();

  const { data: sharedProgrammes } = useGetSharedProgrammesQuery(
    healthProSpace?.id,
    {
      skip: !healthProSpace,
    },
  );

  const { data: ownedProgrammesUnsorted } = useGetProgrammesQuery(undefined);

  const ownedProgrammes = useMemo(() => {
    if (!ownedProgrammesUnsorted) {
      return [];
    }
    const ownedProgrammesSorted = [...ownedProgrammesUnsorted];
    ownedProgrammesSorted.sort(programmesComparer);
    return ownedProgrammesSorted;
  }, [ownedProgrammesUnsorted]);

  const programme = (ownedProgrammes || []).find(
    (p) => p.id === selectedProgrammeId,
  );

  const { data: contentEntries } = useGetContentEntriesByParentQuery(
    programme?.id || '',
    {
      skip: !programme?.id,
    },
  );

  const [createProgramme] = useCreateProgrammeMutation();

  const [updateProgramme] = useUpdateProgrammeMutation();

  const [deleteProgramme] = useDeleteProgrammeMutation();

  const [updateMeal] = useUpdateMealMutation();

  const [lazyGetFullMealsByParents] = useLazyGetFullMealsByParentsQuery();

  const [updateSpace] = useUpdateSpaceMutation();

  const [deleteNote] = useDeleteNoteMutation();

  const [deleteContentEntry] = useDeleteContentEntryMutation();

  const [createContentEntry] = useCreateContentEntryMutation();

  const [createSharedContentEntry] = useCreateSharedContentEntryMutation();

  const [createSharedProgramme] = useCreateSharedProgrammeMutation();

  const [updateSharedProgramme] = useUpdateSharedProgrammeMutation();

  const [deleteSharedProgramme] = useDeleteSharedProgrammeMutation();

  const { prevPlanId, nextPlanId } = programmePlanPaginationIDs(
    programme,
    selectedPlanId,
  );

  const [
    publishConfirmationNotificationVisible,
    setPublishConfirmationNotificationVisible,
  ] = useState(false);

  const [recommenderWizardVisible, setRecommenderWizardVisible] =
    useState(false);

  const currentPLan =
    programme && programme.plans.find((pl) => pl.id === selectedPlanId);
  const currentPlanIndex = programme
    ? programme.plans.findIndex((pl) => pl.id === selectedPlanId)
    : -1;
  const nextPlanIdAfterRemoval = nextPlanId || prevPlanId;

  const friendlyUsername = useSelector(
    userFriendlyNameForActivitySignalSelector,
  );

  if (!programme) {
    return null;
  }

  const actualPlanId = selectedPlanId || programme.plans[0].id;

  const onCreateProgramme = () => {
    navigate(`/programmes/create`);
  };

  const onEditProgramme = () => {
    navigate(`/programmes/${selectedProgrammeId}/edit`);
  };

  const onEditNutritionConstraints = () => {
    navigate(`/programmes/${selectedProgrammeId}/nutritionConstraints/edit`);
  };

  const onEditPlan = () => {
    navigate(`/programmes/${selectedProgrammeId}/plans/${actualPlanId}/edit`);
  };

  /** TODO duplicate code with TopNavBarMobileProgrammes */
  const onDuplicateProgramme = async () => {
    const createdProgramme = await duplicateProgramme(
      programme,
      contentEntries,
      createProgramme,
      updateProgramme,
      createContentEntry,
    );
    navigate(`/programmes/${createdProgramme.id}`);
  };

  const onRemoveProgramme = async () => {
    // const mealIDs = (programme.recipesBoard?.menus || []).flatMap(
    //   (menu) => menu.mealIDs,
    // );
    // dispatch(mealsRemovedFromMealBasket(mealIDs));
    const firstRemainingProgramme = ownedProgrammes.find(
      (b) => b.id !== selectedProgrammeId,
    );
    await removeProgramme(
      programme,
      deleteNote,
      deleteContentEntry,
      deleteProgramme,
    );
    navigate(`/programmes/${firstRemainingProgramme.id}`);
  };

  const onRemoveCurrentWeek = async () => {
    reportAccountActivitySignalOperation({
      spaceID: healthProSpace.id,
      friendlyUsername,
      lastActivityObjectType: ActivityObjectType.PROGRAMMES,
      lastActivityObjectID: programme.id,
    });
    await removePlan(
      programme,
      selectedPlanId,
      deleteNote,
      deleteContentEntry,
      updateProgramme,
    );
    navigate(
      `/programmes/${selectedProgrammeId}/plans/${nextPlanIdAfterRemoval}`,
    );
  };

  const onCreateNewWeek = async () => {
    const programmeId = selectedProgrammeId;
    const addAfterProgrammePlanId = selectedPlanId;
    const programmePlan = {
      title: `${PROGRAMME_PLANS_TITLE_PREFIX} ${currentPlanIndex + 2}`,
      id: uuidv4(),
      shortDescription: '',
      description: null,
      coverImageUrl: null,
      days: emptyProgrammePlan(),
      nutritionConstraints: null,
      showNutritionToUsers: null,
    };
    const updatedProgramme = programmeWithPlanAdded(
      programme,
      programmePlan,
      addAfterProgrammePlanId,
    );
    reportAccountActivitySignalOperation({
      spaceID: healthProSpace.id,
      friendlyUsername,
      lastActivityObjectType: ActivityObjectType.PROGRAMMES,
      lastActivityObjectID: programme.id,
    });
    await updateProgramme(updatedProgramme);
    const { prevPlanId: newPrevPlanId, nextPlanId: newNextPlanId } =
      programmePlanPaginationIDs(
        ownedProgrammes.find((pr) => pr.id === programmeId),
        addAfterProgrammePlanId,
      );
    const navigateToPlanId = newNextPlanId || newPrevPlanId;
    navigate(`/programmes/${selectedProgrammeId}/plans/${navigateToPlanId}`);
  };

  const onRemoveMealsAndGRCRecipesFromPlan = async () => {
    reportAccountActivitySignalOperation({
      spaceID: healthProSpace.id,
      friendlyUsername,
      lastActivityObjectType: ActivityObjectType.PROGRAMMES,
      lastActivityObjectID: programme.id,
    });
    return removeMealsAndGRCRecipesFromProgrammePlan(
      programme,
      selectedPlanId,
      updateProgramme,
    );
  };

  const onClickPublish = () => {
    setPublishConfirmationNotificationVisible(true);
  };

  const onPublishAndNotify = async (
    shouldSendPushNotification,
    notificationTitle,
    notificationText,
  ) => {
    setPublishConfirmationNotificationVisible(false);
    await publishLatestVersionOfProgramme(
      programme,
      sharedProgrammes,
      contentEntries,
      healthProSpace,
      locale,
      createSharedContentEntry,
      createSharedProgramme,
      updateSharedProgramme,
      updateProgramme,
      updateSpace,
    );
    dispatch(
      showGlobalSnackbar({
        notificationText: 'Your program has been published successfully',
      }),
    );
    if (shouldSendPushNotification) {
      console.log('Sending push notification');
      sendNotificationToAllDevicesOperation(
        notificationTitle,
        notificationText,
      );
    }
  };

  const onUnpublish = async () => {
    await unpublishProgramme(
      programme.id,
      sharedProgrammes,
      healthProSpace,
      deleteSharedProgramme,
      updateSpace,
    );
    dispatch(
      showGlobalSnackbar({
        notificationText: 'Your program has been unpublished',
      }),
    );
  };

  const onChangePlan = (newPlanId) => {
    navigate(`/programmes/${programme.id}/plans/${newPlanId}`);
  };

  const onRecommendMeals = async (numDaysToPlan, startDayIndexOneBased) => {
    reportAccountActivitySignalOperation({
      spaceID: healthProSpace.id,
      friendlyUsername,
      lastActivityObjectType: ActivityObjectType.PROGRAMMES,
      lastActivityObjectID: programme.id,
    });
    programmePlanRecommendMeals(
      programme,
      selectedPlanId,
      numDaysToPlan,
      startDayIndexOneBased,
      userLocale(userProfile),
      lazyGetFullMealsByParents,
      updateMeal,
      updateProgramme,
      (notificationText) => dispatch(showGlobalSnackbar({ notificationText })),
    );
  };

  const onExport = () => {
    // dispatch(exportProgrammeAndObjectsAction(programme.id));
  };

  const onImport = () => {
    // TODO
  };

  const sharedProgrammePrevVersion = (sharedProgrammes || []).find(
    (sp) => sp.programmeID === programme.id,
  );

  const isPublished = !!sharedProgrammePrevVersion;

  return (
    <>
      <div className="topNavBarMobile">
        <div
          style={{
            textAlign: 'left',
            margin: 'auto 0 auto 10px',
            color: 'var(--app-primary-font-color)',
          }}>
          <ProgrammesPicker selectedProgrammeId={selectedProgrammeId} />
        </div>
        <div style={{ color: 'var(--app-primary-font-color)', margin: 'auto' }}>
          <PlanNavigator
            currentPlanId={actualPlanId}
            plans={programme.plans}
            onChangePlan={onChangePlan}
            arrowStyle={{ color: 'var(--app-primary-font-color)' }}
          />
        </div>
        <ProgrammesActionsPopupMenu
          isPublished={isPublished}
          fabStyle={{
            position: 'fixed',
            bottom: '67px',
            right: '10px',
            color: 'var(--app-primary-font-color)',
          }}
          isRemoveCurrentWeekDisabled={programme.plans.length === 1}
          onRemoveCurrentWeek={onRemoveCurrentWeek}
          currentWeekTitle={currentPLan.title}
          onCreateNewWeek={onCreateNewWeek}
          onCreateProgramme={onCreateProgramme}
          onImport={onImport}
          onEditProgramme={onEditProgramme}
          onEditPlan={onEditPlan}
          onDuplicateProgramme={onDuplicateProgramme}
          onEditNutritionConstraints={onEditNutritionConstraints}
          onRecommendMeals={() => setRecommenderWizardVisible(true)}
          onRemoveMealsAndGRCRecipesFromPlan={
            onRemoveMealsAndGRCRecipesFromPlan
          }
          onPublish={onClickPublish}
          onUnpublish={onUnpublish}
          onExport={onExport}
          onRemoveProgramme={onRemoveProgramme}
        />
      </div>
      {recommenderWizardVisible && (
        <RecommenderWizardContainer
          onRecommendMeals={onRecommendMeals}
          onDismiss={() => setRecommenderWizardVisible(false)}
        />
      )}
      {publishConfirmationNotificationVisible && (
        <PublishConfirmationNotificationModal
          titleText="Publish your program"
          promptText="Are you sure you want to publish your program to your customers?"
          notificationTitleTemplate="New program available"
          notificationTextTemplate="Check out the latest program"
          onPublish={onPublishAndNotify}
          onDismiss={() => setPublishConfirmationNotificationVisible(false)}
        />
      )}
    </>
  );
};

TopNavBarMobileProgrammes.propTypes = {
  selectedProgrammeId: PropTypes.string.isRequired,
  selectedPlanId: PropTypes.string,
};

TopNavBarMobileProgrammes.defaultProps = {
  selectedPlanId: null,
};

export default TopNavBarMobileProgrammes;
